import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate, Link } from 'react-router-dom'
import { Suspense, lazy, useState, useEffect } from 'react'
import { TimezoneProvider } from '@t-one-internal/design-system'
import { Card, CardContent, Button, Badge } from '@t-one-internal/design-system'
import { Sonner } from '@t-one-internal/design-system'
import { DynamicSidebar } from './components/DynamicSidebar'
import { AuthProvider, useAuth } from './context/AuthContextBackend'
import { ProtectedRoute } from './components/ProtectedRoute'
import OrganizationDetails from './components/OrganizationDetails'
import RegisterForm from './components/RegisterForm'
import LoginForm from './components/LoginForm'
import TwoFactorSetup from './components/TwoFactorSetup'
import SetPassword from './components/SetPassword'
import VerifyIdentity from './components/VerifyIdentity'
import ForgotPassword from './components/ForgotPassword'
import VerifyEmail from './components/VerifyEmail'

import RecognitionPage from './pages/recognition/RecognitionPage'
import AuthCallback from './pages/AuthCallback'
import ReportsRouter from './pages/reports'
import MyPortalPage from './pages/MyPortalPage'
import MyProfilePage from './pages/MyProfilePage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import DashboardsPage from './pages/DashboardsPage'
import HomePage from './pages/HomePage'
import PublicDashboardPage from './pages/PublicDashboardPage'
import DownloadPage from './pages/DownloadPage'

// Open Enrollment / Public Training pages (no auth)
import TrainingLayout from './pages/training/TrainingLayout'
import TrainingCatalogPage from './pages/training/TrainingCatalogPage'
import TrainingEventDetailPage from './pages/training/TrainingEventDetailPage'
import TrainingPortalPage from './pages/training/TrainingPortalPage'
import TrainingCancelPage from './pages/training/TrainingCancelPage'
import TrainingLearnerPage from './pages/training/TrainingLearnerPage'
import ReportBuilderPage from './pages/reports/ReportBuilderPage'
import ReportBuilderLibraryPage from './pages/reports/ReportBuilderLibraryPage'
import ReportBuilderWizardPage from './pages/reports/ReportBuilderWizardPage'
import ReportBuilderViewerPage from './pages/reports/ReportBuilderViewerPage'
import CollabAuthRedirect from './pages/CollabAuthRedirect'
import MySpaceLayout from './components/my-space/Layout'
import { setAuthToken } from "./api/api"
import { getHealthCheck } from "./api/user"
import { useSubscriptionLoader } from './hooks/useSubscriptionLoader'
import { useSubscriptionStore } from './stores/subscriptionStore'
import { TopBar } from './components/TopBar'
import ImpersonationBanner from './components/ImpersonationBanner'
import ImpersonationCallback from './pages/ImpersonationCallback'
import MarketplaceSSOCallback from './pages/MarketplaceSSOCallback'
import { NotificationBanner } from './components/notifications'
import { SubscriptionGuard } from './components/SubscriptionGuard'

// Backward-compat redirect: old /review-cloud/* URLs → /performspark/*
const ReviewCloudRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/review-cloud', '/performspark');
  return <Navigate to={newPath + location.search + location.hash} replace />;
};

// Admin Guard to prevent admin users from accessing main app
const AdminGuard = ({ children }: { children: React.ReactNode }) => {
  const adminToken = localStorage.getItem('admin_access_token');
  const impersonationData = localStorage.getItem('impersonation_data');
  const regularToken = localStorage.getItem('access_token');

  // If impersonating, allow access
  if (impersonationData) {
    return <>{children}</>;
  }

  // If user has a regular access_token, they logged in as a regular user — allow access
  // Clear stale admin token to prevent future conflicts
  if (regularToken && adminToken) {
    localStorage.removeItem('admin_access_token');
    return <>{children}</>;
  }

  // If only admin token exists (no regular login), redirect to admin portal
  if (adminToken) {
    return <Navigate to="/tnadmin/client" replace />;
  }

  return <>{children}</>;
};

// HR Guard to prevent non-HR users from accessing workforce module
const HRGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  // Superadmin bypasses all checks
  if (user?.is_superadmin) {
    return <>{children}</>;
  }

  // If user is not HR manager AND not HRBP, redirect to home
  // Note: Keycloak sends 'is_rc_hrbp', we also check generic 'is_hrbp' just in case
  if (!user?.is_hrmanager && !user?.is_hrbp && !user?.is_rc_hrbp) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};


// Manager Guard to prevent regular employees from accessing dashboards
const ManagerGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  // Superadmin bypasses all checks
  if (user?.is_superadmin) {
    return <>{children}</>;
  }

  // Allow if HR or Manager
  if (user?.is_hrmanager || user?.is_hrbp || user?.is_rc_hrbp || user?.is_rc_manager) {
    return <>{children}</>;
  }

  // Redirect to my-portal if not authorized
  return <Navigate to="/home/my-portal" replace />;
};


// Lazy load remote components — wrap with catch so missing remotes don't crash the shell
const safeLazy = (loader: () => Promise<any>) =>
  lazy(() =>
    loader().catch(() => ({
      default: () => (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <p>This module is not available. Start the corresponding dev server to load it.</p>
        </div>
      ),
    }))
  )

const ReviewCloud = safeLazy(() => import('reviewCloud/App'))
const Workforce = safeLazy(() => import('workforce/App'))
const Learn = safeLazy(() => import('learn/App'))
const AdminPortal = safeLazy(() => import('adminPortal/App'))
const CompBuilder = safeLazy(() => import('compBuilder/App'))
const CustomersPage = lazy(() => import('./customers/pages/CustomersPage'))
const CustomerAddPage = lazy(() => import('./customers/pages/CustomerAddPage'))
const CustomerDetailsPage = lazy(() => import('./customers/id/[id]/page'))

// Loading component
const Loading = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
)

// Navigation component removed (was unused)

// Home component
const Home = () => {
  const [backendResult, setBackendResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const testBackendConnection = async () => {
    setIsLoading(true);
    setBackendResult(null);

    try {
      console.log('🧪 Testing backend connection...');
      const result = await getHealthCheck();
      setBackendResult(result);

      if (result.success) {
        console.log('✅ Backend test successful:', result.data);
      } else {
        console.error('❌ Backend test failed:', result.error);
      }
    } catch (error) {
      console.error('❌ Backend test error:', error);
      setBackendResult({
        success: false,
        error: 'Network error or server unavailable'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8 space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-foreground">
          🎉 Module Federation Success!
        </h1>
        <p className="text-muted-foreground text-lg">
          This shell app hosts multiple micro-frontends using Module Federation
        </p>
        <div className="flex justify-center gap-2">
          <Badge variant="outline">Shell App (Port 3004)</Badge>
          <Badge variant="secondary">Design System Shared</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-2">Customer Portal</h3>
            <p className="text-muted-foreground mb-4">
              Remote micro-frontend running on port 3001
            </p>
            <Button asChild>
              <Link to="/customer">Open Customer Portal</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-2">PerformSpark</h3>
            <p className="text-muted-foreground mb-4">
              Remote micro-frontend running on port 3003
            </p>
            <Button asChild>
              <Link to="/performspark/cycle-management/overview">Open PerformSpark</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-2">Workforce</h3>
            <p className="text-muted-foreground mb-4">
              Remote micro-frontend running on port 3007
            </p>
            <Button asChild>
              <Link to="/workforce">Open Workforce</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-2">Learn</h3>
            <p className="text-muted-foreground mb-4">
              Remote micro-frontend running on port 3008
            </p>
            <Button asChild>
              <Link to="/learn">Open Learn</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-2">Comp Builder</h3>
            <p className="text-muted-foreground mb-4">
              Remote micro-frontend running on port 3009
            </p>
            <Button asChild>
              <Link to="/comp-builder/job-builder">Open Comp Builder</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-2">🔗 Backend Test</h3>
            <p className="text-muted-foreground mb-4">
              Test authentication with Go backend on port 5000
            </p>
            <div className="space-y-3">
              <Button
                onClick={testBackendConnection}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? '🔄 Testing...' : '🧪 Test Backend'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold mb-4">Architecture</h3>
          <div className="space-y-2 text-sm">
            <p>🏠 <strong>Shell App:</strong> Host application (this page)</p>
            <p>📦 <strong>Design System:</strong> Shared components across all apps</p>
            <p>🌐 <strong>Customer Portal:</strong> Independent micro-frontend</p>
            <p>☁️ <strong>PerformSpark:</strong> Performance review micro-frontend</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Timezone wrapper that uses account timezone
const TimezoneWrapper = ({ children }: { children: React.ReactNode }) => {
  const { account } = useAuth()
  // Get timezone from account, fallback to America/New_York (US Eastern Time)
  const timezone = (account as any)?.timezone || 'America/New_York'

  return (
    <TimezoneProvider timezone={timezone}>
      {children}
    </TimezoneProvider>
  )
}

const AppContent = () => {
  const { user, account, token, isAuthenticated } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  console.log("user", user)

  // Load subscriptions when authenticated
  const { isLoading: isLoadingSubscriptions } = useSubscriptionLoader(
    account?.account_uuid,
    isAuthenticated
  )

  // Prefetch only authenticated, subscribed remotes (workforce first, then others).
  usePrefetchModules(isAuthenticated)

  // Detect external collaborator context: either collaborator landing page or
  // Job Builder edit/my-collaborations routes with a collaborator token
  const searchParams = new URLSearchParams(location.search)
  const hasCollaboratorToken = !!searchParams.get('token')
  const hasCollaboratorMode = localStorage.getItem('collaborator_mode') === 'true'
  const isCollaboratorContext =
    location.pathname.includes('/comp-builder/job-builder/collaborator/') ||
    (location.pathname.includes('/comp-builder/job-builder/edit/') && (hasCollaboratorToken || hasCollaboratorMode)) ||
    (location.pathname.includes('/comp-builder/job-builder/my-collaborations') && (hasCollaboratorToken || hasCollaboratorMode)) ||
    (location.pathname.includes('/comp-builder/job-builder/titles') && hasCollaboratorMode)

  // Sync auth token for regular users, but do NOT overwrite collaborator JWT
  useEffect(() => {
    if (isCollaboratorContext) return
    setAuthToken(token || null)
  }, [token, isCollaboratorContext])
  // Redirect removed to allow Home Page to render for all users
  // useEffect(() => {
  //   if (isAuthenticated && location.pathname === '/') {
  //     navigate('/home/my-portal', { replace: true });
  //   }
  // }, [isAuthenticated, location.pathname, navigate]);

  console.log('🔍 Shell App - User:', user)
  console.log('🔍 Shell App - Account:', account)
  console.log('🔍 Shell App - Token:', token)

  const handleNavigate = (path: string) => {
    // Handle navigation to different micro-frontends
    console.log('Navigating to:', path)
    navigate(path);
  }

  // Hide sidebar for admin portal
  const isAdminPortal = location.pathname.startsWith('/tnadmin')
  // Public training pages render outside the shell layout (no sidebar, topbar, or overflow constraints)
  const isTrainingPublic = location.pathname.startsWith('/training/')
  // Change password page renders full-screen (no sidebar/topbar)
  const isChangePassword = location.pathname === '/change-password'

  // Show loading state while subscriptions are being fetched
  if (isLoadingSubscriptions && !isAdminPortal) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your workspace...</p>
        </div>
      </div>
    )
  }


  // Public training pages: catalog/event/cancel are standalone (no sidebar),
  // portal/registration use TrainingLayout (sidebar + topbar) for authenticated learners
  if (isTrainingPublic) {
    return (
      <Routes>
        {/* Standalone pages — full-width, no sidebar */}
        <Route path="/training/catalog" element={<TrainingCatalogPage />} />
        <Route path="/training/event/:id/detail" element={<TrainingEventDetailPage />} />
        <Route path="/training/event/share/:token" element={<TrainingEventDetailPage />} />
        <Route path="/training/cancel/:cancelToken" element={<TrainingCancelPage />} />

        {/* Sidebar layout — for authenticated learners (magic token) */}
        <Route path="/training" element={<TrainingLayout />}>
          <Route path="portal" element={<TrainingPortalPage />} />
          <Route path="registration/:enrollmentId" element={<TrainingLearnerPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Impersonation banner - always at the very top */}
      <ImpersonationBanner />

      {/* Notification Banner - full-width, right after impersonation banner */}
      {!isAdminPortal && !isChangePassword && <NotificationBanner />}

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        {!isAdminPortal && !isChangePassword && <DynamicSidebar onNavigate={handleNavigate} />}

        <div
          className="flex-1 flex flex-col min-w-0 transition-[margin-left] duration-300 ease-in-out"
        >
          {!isAdminPortal && !isChangePassword && <TopBar />}

          <main className="flex-1 overflow-y-auto">
            <Routes>
              <Route path="/" element={<Navigate to="/home/my-portal" replace />} />
              {/* Home route - top level, no /my-space prefix */}
              <Route path="/home" element={<MySpaceLayout />}>
                <Route path="*" element={<HomePage />} />
              </Route>
              {/* My Space routes with breadcrumb layout */}
              <Route path="/my-space" element={<MySpaceLayout />}>
                <Route path="my-portal" element={<MyPortalPage />} />
                <Route path="dashboards" element={<Navigate to="/analytics/dashboards" replace />} />
                <Route path="shared-dashboards" element={<Navigate to="/analytics/dashboards?tab=shared-dashboards" replace />} />
                <Route path="recognitions" element={<RecognitionPage />} />
              </Route>
              <Route path="/portal" element={<div className="p-8"><h1 className="text-2xl font-bold">My Portal</h1><p>This will host the portal micro-frontend</p></div>} />
              <Route path="/organization" element={<Navigate to="/organization/settings" replace />} />
              {/* <Route path="/organization/details" element={<OrganizationDetails />} /> */}
              <Route path="/organization/*" element={<HRGuard><Suspense fallback={<Loading />}><Workforce user={user} account={account} token={token} /></Suspense></HRGuard>} />
              <Route path="/organisation" element={<Navigate to="/organization/settings" replace />} />
              <Route path="/organisation/*" element={<HRGuard><Suspense fallback={<Loading />}><Workforce user={user} account={account} token={token} /></Suspense></HRGuard>} />
              <Route path="/my-profile" element={<MyProfilePage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/cycles" element={<div className="p-8"><h1 className="text-2xl font-bold">Cycles</h1><p>This will host the cycles micro-frontend</p></div>} />
              <Route path="/performance" element={<div className="p-8"><h1 className="text-2xl font-bold">Performance</h1><p>This will host the performance micro-frontend</p></div>} />
              <Route path="/goal-management" element={<div className="p-8"><h1 className="text-2xl font-bold">Goals</h1><p>This will host the goals micro-frontend</p></div>} />
              <Route path="/talent" element={<div className="p-8"><h1 className="text-2xl font-bold">Talent</h1><p>This will host the talent micro-frontend</p></div>} />
              {/* Communications routes */}
              {/* <Route path="/organisation/communications" element={<CommunicationsLayout />}>
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="templates/new" element={<EditTemplatePage />} />
              <Route path="templates/:id" element={<EditTemplatePage />} />
              <Route path="templates/:id/logs" element={<TemplateLogsPage />} />
              <Route path="triggers" element={<TriggersPage />} />
              <Route path="triggers/new" element={<EditTriggerPage />} />
              <Route path="triggers/:id" element={<EditTriggerPage />} />
              <Route path="logs" element={<LogsPage />} />
              <Route index element={<Navigate to="notifications" replace />} />
            </Route> */}
              {/* <Route
              path="/customer/*"
              element={
                <Suspense fallback={<Loading />}>
                  <CustomerPortal user={user} token={token} />
                </Suspense>
              }
            /> */}
              <Route
                path="/performspark/*"
                element={
                  <SubscriptionGuard moduleId="perform-spark">
                    <Suspense fallback={<Loading />}>
                      <ReviewCloud user={user} token={token} />
                    </Suspense>
                  </SubscriptionGuard>
                }
              />
              {/* Backward-compat redirect: old /review-cloud/* URLs → /performspark/* */}
              <Route path="/review-cloud/*" element={<ReviewCloudRedirect />} />

              <Route
                path="/workforce/*"
                element={
                  <Suspense fallback={<Loading />}>
                    <Workforce user={user} account={account} token={token} />
                  </Suspense>
                }
              />
              <Route
                path="/integrations/*"
                element={
                  <HRGuard>
                    <Suspense fallback={<Loading />}>
                      <Workforce user={user} account={account} token={token} />
                    </Suspense>
                  </HRGuard>
                }
              />
              <Route path="/learn/analytics" element={
                <SubscriptionGuard moduleId="learn">
                  <DashboardsPage pageKey="learn" />
                </SubscriptionGuard>
              } />
              <Route
                path="/learn/*"
                element={
                  <SubscriptionGuard moduleId="learn">
                    <Suspense fallback={<Loading />}>
                      <Learn user={user} token={token} />
                    </Suspense>
                  </SubscriptionGuard>
                }
              />
              <Route
                path="/comp-builder/*"
                element={
                  <SubscriptionGuard moduleId="comp-builder">
                    <Suspense fallback={<Loading />}>
                      <CompBuilder />
                    </Suspense>
                  </SubscriptionGuard>
                }
              />
              <Route path="/analytics/dashboards" element={<ManagerGuard><DashboardsPage /></ManagerGuard>} />
              <Route path="/analytics/report-builder" element={<ReportBuilderLibraryPage />} />
              <Route path="/analytics/report-builder/new" element={<ReportBuilderWizardPage />} />
              <Route path="/analytics/report-builder/classic" element={<ReportBuilderPage />} />
              <Route path="/analytics/report-builder/:id" element={<ReportBuilderViewerPage />} />
              <Route path="/analytics/report-builder/:id/edit" element={<ReportBuilderWizardPage />} />
              <Route path="/analytics/reports/*" element={<ReportsRouter />} />
              {/* Authenticated report download */}
              <Route path="/download/report/:id" element={<DownloadPage type="authenticated" />} />
              {/* Customer Management Routes */}
              <Route
                path="/customers"
                element={
                  <Suspense fallback={<Loading />}>
                    <CustomersPage />
                  </Suspense>
                }
              />
              <Route
                path="/customers/add"
                element={
                  <Suspense fallback={<Loading />}>
                    <CustomerAddPage />
                  </Suspense>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <Suspense fallback={<Loading />}>
                    <CustomerDetailsPage />
                  </Suspense>
                }
              />

              {/* Recognition / Kudos module - redirect old path */}
              <Route path="/recognition" element={<Navigate to="/my-space/recognitions" replace />} />

              <Route path="*" element={<div className="p-8">404 - Page Not Found</div>} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  )
}

// Prefetch remote modules only for authenticated users, staged by subscription.
// Step 1: workforce (core HR, always needed) loads on idle right after auth.
// Step 2: review-cloud / learn / comp-builder load only if the account subscribes,
//         and only once the subscription store has finished its first fetch.
// Admin portal stays fully lazy — gated by a separate admin token flow.
const SUBSCRIPTION_REMOTE_LOADERS: Record<string, () => Promise<unknown>> = {
  'review-cloud': () => import('reviewCloud/App'),
  'learn': () => import('learn/App'),
  'comp-builder': () => import('compBuilder/App'),
};

const idle = (cb: () => void, timeout = 2000) => {
  if (typeof window === 'undefined') return;
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === 'function') ric(cb, { timeout });
  else setTimeout(cb, 200);
};

const usePrefetchModules = (isAuthenticated: boolean) => {
  const subscriptions = useSubscriptionStore((s) => s.subscriptions);
  const isLoadingSubs = useSubscriptionStore((s) => s.isLoading);
  const lastFetched = useSubscriptionStore((s) => s.lastFetched);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Kick workforce prefetch as soon as we're authenticated — it's the core
    // module and the most likely first destination post-login.
    idle(() => {
      import('workforce/App').catch(() => { });
    }, 1000);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Wait until subscriptions have been fetched at least once. `lastFetched`
    // is hydrated from localStorage, so repeat visitors don't wait.
    if (isLoadingSubs) return;
    if (!lastFetched) return;

    const enabledIds = subscriptions
      .filter((m) => m.submodules.some((s) => s.is_enabled))
      .map((m) => m.module_id);

    const loaders = enabledIds
      .map((id) => SUBSCRIPTION_REMOTE_LOADERS[id])
      .filter((fn): fn is () => Promise<unknown> => typeof fn === 'function');

    if (loaders.length === 0) return;

    // Stagger: give workforce + initial paint some room before pulling the rest.
    const timer = setTimeout(() => {
      idle(() => {
        loaders.forEach((fn) => { fn().catch(() => { }); });
      }, 5000);
    }, 1500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, isLoadingSubs, lastFetched, subscriptions]);
};

function App() {
  return (
    <>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes - not protected */}
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<SetPassword />} />
            <Route path="/verify-identity" element={<VerifyIdentity />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/2fa-setup" element={<TwoFactorSetup />} />
            {/* Impersonation callback - processes impersonation params */}
            <Route path="/impersonation/callback" element={<ImpersonationCallback />} />
            {/* Marketplace SSO - auto-login buyer from Exchange marketplace */}
            <Route path="/marketplace-sso" element={<MarketplaceSSOCallback />} />
            {/* Public shared dashboard */}
            <Route path="/shared/:token" element={<PublicDashboardPage />} />

            {/* Public report download (token-based) */}
            <Route path="/reports/download/:token" element={<DownloadPage type="public" />} />

            {/* Open Enrollment — Public training pages (no auth) */}
            <Route path="/training/catalog" element={<TrainingCatalogPage />} />
            <Route path="/training/event/:id/detail" element={<TrainingEventDetailPage />} />
            <Route path="/training/event/share/:token" element={<TrainingEventDetailPage />} />
            <Route path="/training" element={<TrainingLayout />}>
              <Route path="portal" element={<TrainingPortalPage />} />
              <Route path="registration/:enrollmentId" element={<TrainingLearnerPage />} />
            </Route>
            <Route path="/training/cancel/:cancelToken" element={<TrainingCancelPage />} />

            {/* Public attendance signing page — token-based, no auth */}
            <Route
              path="/learn/attendance/sign/:token"
              element={
                <Suspense fallback={<Loading />}>
                  <Learn />
                </Suspense>
              }
            />

            {/* Public registration page — token-based, no auth */}
            <Route
              path="/learn/public/register/:token"
              element={
                <Suspense fallback={<Loading />}>
                  <Learn />
                </Suspense>
              }
            />

            {/* Legacy collaborator authentication redirect */}
            <Route path="/CollabAuth" element={<CollabAuthRedirect />} />

            {/* Public routes - accessible without authentication */}
            <Route
              path="/public/*"
              element={
                <Suspense fallback={<Loading />}>
                  <ReviewCloud />
                </Suspense>
              }
            />

            {/* Admin portal - has its own auth, completely separate from main app */}
            <Route
              path="/tnadmin/*"
              element={
                <Suspense fallback={<Loading />}>
                  <AdminPortal />
                </Suspense>
              }
            />

            {/* Protected routes (for regular users) - prevent access if admin token exists */}
            <Route path="/*" element={
              <ProtectedRoute>
                <AdminGuard>
                  <TimezoneWrapper>
                    <AppContent />
                  </TimezoneWrapper>
                </AdminGuard>
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Sonner position="top-right" style={{ top: '50px' }} />
    </>
  )
}

export default App
